import { useMemo } from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import type { CoverageHistoryTrendPoint } from '@/components/smartai/api/getCoverageHistory';
import {
  formatCountCompact,
  stackCountsFromTrendPoint,
  trendBuildBarAxisTickLines,
  trendRunBarAxisTickLines,
  chartMetricToKind,
  type CoverageChartMetric,
  type CoverageMetricKind,
} from './coverage-history-utils';
import { trendChartPanelClass } from './coverage-trend-layout';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const LINE_COLORS = { covered: '#4b6bec', uncovered: '#f4a096' };
const INVENTORY_COLORS = { covered: '#16a34a', uncovered: '#fde68a' };

const barTotalPlugin: Plugin<'bar'> = {
  id: 'coverageByBuildTotals',
  afterDatasetsDraw(chart) {
    const uncovered = chart.data.datasets[0]?.data as number[] | undefined;
    const covered = chart.data.datasets[1]?.data as number[] | undefined;
    if (!covered?.length || !uncovered?.length) return;
    const meta = chart.getDatasetMeta(1);
    const { ctx } = chart;
    meta.data.forEach((bar, i) => {
      const total = (Number(covered[i]) || 0) + (Number(uncovered[i]) || 0);
      if (!total || bar.x == null || bar.y == null) return;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.font = '600 11px system-ui, sans-serif';
      ctx.fillStyle = '#475569';
      ctx.fillText(formatCountCompact(total), bar.x, bar.y - 6);
      ctx.restore();
    });
  },
};

interface BuildStackedBarChartProps {
  points: CoverageHistoryTrendPoint[];
  measure: CoverageMetricKind;
  axisTickLines: (point: CoverageHistoryTrendPoint) => string[];
  title: string;
  colors?: { covered: string; uncovered: string };
}

function BuildStackedBarChart({
  points,
  measure,
  axisTickLines,
  title,
  colors = measure === 'inventory' ? INVENTORY_COLORS : LINE_COLORS,
}: BuildStackedBarChartProps) {
  const chartData = useMemo<ChartData<'bar'>>(() => {
    const covered = points.map(p => stackCountsFromTrendPoint(p, measure).covered);
    const uncovered = points.map(
      p => stackCountsFromTrendPoint(p, measure).uncovered,
    );
    return {
      labels: points.map(() => ''),
      datasets: [
        {
          label: measure === 'lines' ? 'Uncovered lines' : 'Uncovered elements',
          data: uncovered,
          backgroundColor: colors.uncovered,
          borderRadius: { topLeft: 0, topRight: 0, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false,
          stack: 'build',
        },
        {
          label: measure === 'lines' ? 'Covered lines' : 'Covered elements',
          data: covered,
          backgroundColor: colors.covered,
          borderRadius: { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false,
          stack: 'build',
        },
      ],
    };
  }, [points, measure, colors.covered, colors.uncovered]);

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 20, bottom: 4 } },
      datasets: {
        bar: {
          maxBarThickness: 48,
          categoryPercentage: 0.55,
          barPercentage: 0.9,
        },
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            padding: 16,
            font: { size: 11 },
            useBorderRadius: true,
            borderRadius: 2,
            sort(a, b) {
              const coveredLabel =
                measure === 'lines' ? 'Covered lines' : 'Covered elements';
              if (a.text === coveredLabel) return -1;
              if (b.text === coveredLabel) return 1;
              return 0;
            },
          },
        },
        tooltip: {
          callbacks: {
            title(items) {
              const idx = items[0]?.dataIndex;
              if (idx == null || idx < 0 || idx >= points.length) return '';
              return axisTickLines(points[idx]).join(' · ');
            },
            label(ctx) {
              const v = ctx.parsed.y;
              if (v == null) return '';
              return `${ctx.dataset.label}: ${Number(v).toLocaleString()}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          border: { display: false },
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
            padding: 8,
            font: { size: 10 },
            callback(_tickValue, index) {
              const point = points[index];
              if (!point) return '';
              const lines = axisTickLines(point);
              return lines.length > 1 ? lines : lines[0] ?? '';
            },
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: 'rgba(43, 33, 24, 0.06)' },
          border: { display: false },
          ticks: {
            font: { size: 11 },
            callback(value) {
              const n = typeof value === 'number' ? value : Number(value);
              return formatCountCompact(n);
            },
          },
        },
      },
    }),
    [points, measure, axisTickLines],
  );

  return (
    <div className={trendChartPanelClass}>
      <h4 className="m-0 mb-4 text-[0.9375rem] font-semibold text-[var(--inv-text)]">
        {title}
      </h4>
      <div className="h-[min(16rem,40vh)] w-full min-w-0">
        <Bar data={chartData} options={options} plugins={[barTotalPlugin]} />
      </div>
    </div>
  );
}

interface CoverageByBuildChartProps {
  points: CoverageHistoryTrendPoint[];
  metric: CoverageChartMetric;
  axisVariant?: 'build' | 'run';
  title?: string;
}

export default function CoverageByBuildChart({
  points,
  metric,
  axisVariant = 'build',
  title,
}: CoverageByBuildChartProps) {
  const axisTickLines =
    axisVariant === 'run' ? trendRunBarAxisTickLines : trendBuildBarAxisTickLines;

  if (!points.length) {
    return null;
  }

  const baseTitle =
    title ?? (axisVariant === 'run' ? 'Coverage by run' : 'Coverage by build');

  if (metric === 'both') {
    return (
      <div className="grid gap-6">
        <BuildStackedBarChart
          points={points}
          measure="lines"
          axisTickLines={axisTickLines}
          title={`${baseTitle} — lines of code`}
          colors={LINE_COLORS}
        />
        <BuildStackedBarChart
          points={points}
          measure="inventory"
          axisTickLines={axisTickLines}
          title={`${baseTitle} — inventory`}
          colors={INVENTORY_COLORS}
        />
      </div>
    );
  }

  const measure = chartMetricToKind(metric);
  return (
    <BuildStackedBarChart
      points={points}
      measure={measure}
      axisTickLines={axisTickLines}
      title={baseTitle}
    />
  );
}
